import { t as getLogger } from "./logger-CpoV6RQj.mjs";
import { n as WINDOW } from "./errorManager-CoSVVhi4.mjs";
import { t as getRequestMonitor } from "./requestMonitor-BI0Un5F-.mjs";

//#region src/interceptors/xhr/index.ts
function setupXHRInterceptorInternal(config = {}) {
	const logger = getLogger();
	if (!WINDOW.XMLHttpRequest || typeof WINDOW.XMLHttpRequest !== "function") {
		logger.warn("XMLHttpRequest not available, skipping XHR interceptor setup");
		return () => {};
	}
	const originalSend = WINDOW.XMLHttpRequest.prototype.send;
	const originalOpen = WINDOW.XMLHttpRequest.prototype.open;
	let requestMonitor;
	try {
		requestMonitor = getRequestMonitor({
			timeoutMs: config.timeoutMs,
			showSlowRequestAlerts: config.showSlowRequestAlerts,
			showErrorAlerts: config.showErrorAlerts,
			monitoredServicePatterns: config.monitoredServicePatterns,
			monitoredServiceOptions: config.monitoredServiceOptions
		});
	} catch (error) {
		logger.error("Failed to initialize request monitor for XHR interceptor:", error);
		return () => {};
	}
	const metadataMap = /* @__PURE__ */ new WeakMap();
	/**
	* Safely extract and store metadata from XHR without affecting its operation
	*/
	function captureXHRMetadata(xhr, url) {
		try {
			const existingMetadata = metadataMap.get(xhr);
			if (existingMetadata) cleanupXHRMetadata(xhr, existingMetadata);
			const urlString = String(url);
			let isMonitored = false;
			try {
				isMonitored = requestMonitor.isMonitored(urlString);
			} catch (error) {
				logger.warn("Error checking if URL is monitored in XHR interceptor:", error);
				isMonitored = false;
			}
			const metadata = {
				url: urlString,
				isMonitored,
				startTime: Date.now()
			};
			metadataMap.set(xhr, metadata);
		} catch (error) {
			logger.warn("Error capturing XHR metadata:", error);
		}
	}
	/**
	* Cleanup metadata and associated resources
	*/
	function cleanupXHRMetadata(xhr, metadata) {
		try {
			if (metadata.timeoutId) {
				WINDOW.clearTimeout(metadata.timeoutId);
				metadata.timeoutId = void 0;
			}
			if (metadata.eventHandlers) {
				xhr.removeEventListener("load", metadata.eventHandlers.load);
				xhr.removeEventListener("error", metadata.eventHandlers.error);
				xhr.removeEventListener("abort", metadata.eventHandlers.abort);
				metadata.eventHandlers = void 0;
			}
			metadataMap.delete(xhr);
		} catch (error) {
			logger.warn("Error cleaning up XHR metadata:", error);
		}
	}
	/**
	* Safely process response data without affecting the original response
	*/
	function processResponse(xhr, eventType) {
		try {
			const metadata = metadataMap.get(xhr);
			if (!metadata || !metadata.isMonitored) {
				if (metadata) cleanupXHRMetadata(xhr, metadata);
				return;
			}
			const responseData = {
				status: 0,
				statusText: "",
				url: metadata.url
			};
			try {
				if (eventType === "load" && "status" in xhr) {
					const status = xhr.status;
					if (typeof status === "number" && !isNaN(status)) responseData.status = status;
				}
				if (eventType === "load" && "statusText" in xhr) {
					const statusText = xhr.statusText;
					if (typeof statusText === "string") responseData.statusText = statusText;
				}
			} catch (error) {
				logger.warn("Error reading XHR response properties:", error);
			}
			WINDOW.queueMicrotask(() => {
				try {
					switch (eventType) {
						case "load":
							requestMonitor.handleHttpError(responseData.status, responseData.statusText, responseData.url, "xhr");
							break;
						case "error":
							requestMonitor.handleNetworkError("XMLHttpRequest encountered an error.", responseData.url, "xhr");
							break;
						case "abort":
							requestMonitor.handleNetworkError("XMLHttpRequest was aborted.", responseData.url, "xhr");
							break;
					}
				} catch (error) {
					logger.warn(`Error in XHR ${eventType} handler:`, error);
				}
			});
			cleanupXHRMetadata(xhr, metadata);
		} catch (error) {
			logger.warn(`Error processing XHR ${eventType} event:`, error);
			try {
				const metadata = metadataMap.get(xhr);
				if (metadata) cleanupXHRMetadata(xhr, metadata);
			} catch (cleanupError) {
				logger.warn("Error during cleanup after processing failure:", cleanupError);
			}
		}
	}
	/**
	* Create a safe event handler that doesn't interfere with existing handlers
	*/
	function createSafeEventHandler(eventType) {
		return function() {
			try {
				processResponse(this, eventType);
			} catch (error) {
				logger.warn(`Error in XHR ${eventType} event handler:`, error);
			}
		};
	}
	WINDOW.XMLHttpRequest.prototype.open = function(method, url, async = true, username, password) {
		let callOriginal = true;
		try {
			captureXHRMetadata(this, url != null ? String(url) : "");
			originalOpen.call(this, method, url, async, username, password);
			callOriginal = false;
		} catch (error) {
			logger.warn("Error in XHR open override:", error);
			const metadata = metadataMap.get(this);
			if (metadata) cleanupXHRMetadata(this, metadata);
			throw error;
		} finally {
			if (callOriginal) originalOpen.call(this, method, url, async, username, password);
		}
	};
	WINDOW.XMLHttpRequest.prototype.send = function(body) {
		try {
			const metadata = metadataMap.get(this);
			if (metadata && metadata.isMonitored) try {
				const handlers = {
					load: createSafeEventHandler("load"),
					error: createSafeEventHandler("error"),
					abort: createSafeEventHandler("abort")
				};
				metadata.eventHandlers = handlers;
				this.addEventListener("load", handlers.load, false);
				this.addEventListener("error", handlers.error, false);
				this.addEventListener("abort", handlers.abort, false);
				if (config.showSlowRequestAlerts && config.showErrorAlerts && config.timeoutMs) try {
					const timeoutMs = requestMonitor.getTimeoutMs();
					if (timeoutMs > 0) metadata.timeoutId = WINDOW.setTimeout(() => {
						try {
							const currentMetadata = metadataMap.get(this);
							if (currentMetadata && currentMetadata === metadata) requestMonitor.handleSlowRequest(metadata.url, "xhr");
						} catch (error) {
							logger.warn("Error handling XHR slow request:", error);
						}
					}, timeoutMs);
				} catch (error) {
					logger.warn("Error setting up XHR timeout monitoring:", error);
				}
			} catch (error) {
				logger.warn("Error setting up XHR monitoring:", error);
				if (metadata) cleanupXHRMetadata(this, metadata);
			}
		} catch (error) {
			logger.warn("Error in XHR send override:", error);
		}
		try {
			originalSend.call(this, body);
		} catch (originalError) {
			try {
				const metadata = metadataMap.get(this);
				if (metadata) cleanupXHRMetadata(this, metadata);
			} catch (cleanupError) {
				logger.warn("Error cleaning up XHR metadata after send failure:", cleanupError);
			}
			throw originalError;
		}
	};
	return () => {
		try {
			WINDOW.XMLHttpRequest.prototype.send = originalSend;
			WINDOW.XMLHttpRequest.prototype.open = originalOpen;
		} catch (error) {
			logger.warn("Error during XHR interceptor cleanup:", error);
		}
	};
}
/**
* XHRInterceptor class that implements the Interceptor interface
*/
var XHRInterceptor = class {
	name = "xhr";
	config;
	logger = getLogger();
	cleanupFn = null;
	constructor(config) {
		this.config = {
			timeoutMs: 3e4,
			showSlowRequestAlerts: true,
			showErrorAlerts: true,
			monitoredServicePatterns: [],
			monitoredServiceOptions: {},
			...config
		};
	}
	setup() {
		try {
			if (this.cleanupFn) {
				this.logger.warn("XHR interceptor already set up, cleaning up previous instance");
				this.cleanupFn();
				this.cleanupFn = null;
			}
			this.cleanupFn = setupXHRInterceptorInternal(this.config);
			return () => {
				if (this.cleanupFn) {
					this.cleanupFn();
					this.cleanupFn = null;
				}
			};
		} catch (error) {
			this.logger.error("XHR interceptor setup failed:", error);
			return () => {};
		}
	}
};
/**
* Sets up an XHR interceptor that monitors network requests without interfering
* with customer business logic. Uses event listeners and microtasks to ensure
* complete isolation from existing code.
*
* @param config - Configuration options for the interceptor.
* @returns A cleanup function that restores the original XHR behavior.
*/
function setupXHRInterceptor(config = {}) {
	const logger = getLogger();
	try {
		return setupXHRInterceptorInternal(config);
	} catch (error) {
		logger.error("XHR interceptor setup failed:", error);
		return () => {};
	}
}

//#endregion
export { XHRInterceptor, setupXHRInterceptor };